import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, BookOpen, Bird, Star, Radio, Image, ScrollText, Sparkles, Tv } from "lucide-react";
import RoomCard from "../components/tavern/RoomCard";
import api from "../lib/api";

const HERO_BG = "https://images.pexels.com/photos/5858038/pexels-photo-5858038.jpeg?auto=compress&cs=tinysrgb&w=1600";
const ORCHID = "https://images.unsplash.com/photo-1477093782505-e10aaeb27c6d?w=900";

const ROOMS = [
  { to:"/about", title:"About the Tavern", subtitle:"the keeper's tale", description:"The story behind Nocturne — a streamer, gamer, storyteller, photographer, and unrepentant collector of strange facts.", icon: BookOpen, testid:"room-about" },
  { to:"/adventures", title:"Adventures", subtitle:"games & streams", description:"What we're playing now, where to find the streams, and recent recordings from the archive.", icon: Compass, testid:"room-adventures" },
  { to:"/ravens", title:"Messenger Ravens", subtitle:"socials & contact", description:"Tiny black birds carrying messages between worlds. Find Nocturne wherever you wander.", icon: Bird, testid:"room-ravens" },
  { to:"/star-charts", title:"Star Charts", subtitle:"the curiosity cabinet", description:"Folklore, astronomy, history, science, museums — a library and observatory in one.", icon: Star, testid:"room-star-charts" },
  { to:"/second-life", title:"Second Life Adventures", subtitle:"virtual worlds", description:"Sim journals, character profiles, photography, home decorating, and quiet roleplay.", icon: Sparkles, testid:"room-second-life" },
  { to:"/radio", title:"Nocturne Radio", subtitle:"a fallout-inspired station", description:"Lore, DJs, and crackling broadcasts from a tower at the edge of the wasteland.", icon: Radio, testid:"room-radio" },
  { to:"/gallery", title:"Gallery", subtitle:"visual archive", description:"Screenshots, SL photography, travel snapshots and creative project memories.", icon: Image, testid:"room-gallery" },
  { to:"/notice-board", title:"Tavern Notice Board", subtitle:"updates & news", description:"Upcoming streams, gaming adventures, website updates, community news, project progress, and discoveries — pinned as they happen.", icon: ScrollText, testid:"room-notice-board" },
];

export default function Home() {
  const [twitch, setTwitch] = useState(null);
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    const fetchTwitch = () => api.get("/twitch/status").then(r => setTwitch(r.data)).catch(()=>{});
    fetchTwitch();
    const id = setInterval(fetchTwitch, 60000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    api.get("/posts").then(r => setPosts(r.data.slice(0,3))).catch(()=>{});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{minHeight:"86vh"}}>
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="lantern-lit tavern" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{
            background:"linear-gradient(180deg, rgba(26,10,46,0.55) 0%, rgba(26,10,46,0.9) 70%, var(--bg-base) 100%)"
          }}/>
          <div className="lantern-glow" style={{top:"15%", left:"8%"}} />
          <div className="lantern-glow" style={{top:"30%", right:"6%"}} />
          <div className="lantern-glow" style={{bottom:"10%", left:"40%"}} />
        </div>

        <div className="relative z-10 page-container fade-in" style={{paddingTop:"7rem"}}>
          <div className="page-eyebrow">✦ a hidden tavern in the moonlit forest ✦</div>
          <h1 className="page-title" style={{fontSize:"clamp(2.8rem, 6vw, 5rem)", lineHeight:1.02}}>
            Nocturne Orchid
          </h1>
          <p className="tavern-script mt-3 text-2xl md:text-3xl" style={{color:"var(--orchid-light)"}}>
            where curiosity blooms after dark.
          </p>
          <p className="page-sub mt-6 text-lg">
            Welcome, traveler. Pull up a chair by the fire. Inside these walls you'll find streams and stories, star charts and folklore,
            a fallout-tinged radio tower, and a community of curious, kind, slightly chaotic souls.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/adventures" className="gold-btn" data-testid="cta-explore">
              <Compass className="h-4 w-4" /> Begin the Adventure
            </Link>

            <a href="https://www.twitch.tv/orchidnocturne" target="_blank" rel="noreferrer" className="flex items-center gap-2 ml-auto" data-testid="twitch-status">
              {twitch?.is_live ? (
                <>
                  <span className="live-dot" />
                  <span className="text-sm truncate max-w-[280px]" style={{color:"var(--gold)"}}>
                    LIVE · {twitch.title || twitch.game || "streaming"}
                  </span>
                </>
              ) : (
                <>
                  <Tv className="h-4 w-4" style={{color:"var(--muted)"}}/>
                  <span className="text-sm" style={{color:"var(--muted)"}}>tavern quiet — stream offline</span>
                </>
              )}
            </a>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="page-container" style={{paddingTop:"3rem"}}>
        <div className="divider-ornament tavern-heading text-xl"><span>✦ The Rooms of the Tavern ✦</span></div>
        <p className="text-center page-sub mx-auto mt-3">Every door opens into a different world. Wander as you please.</p>

        <div className="bento-grid mt-10">
          {ROOMS.map(r => <RoomCard key={r.to} {...r} />)}
        </div>
      </section>

      {/* RECENT WHISPERS */}
      <section className="page-container" style={{paddingTop:"2rem"}}>
        <div className="divider-ornament tavern-heading text-xl"><span>Recent Whispers</span></div>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {posts.map(p => (
            <Link to={`/star-charts`} key={p.id} className="parchment-card p-5 hover:-translate-y-1 transition-transform" data-testid={`whisper-${p.id}`}>
              <div className="page-eyebrow">{p.category.replace("-"," ")}</div>
              <div className="tavern-heading text-xl mt-1 gold-text">{p.title}</div>
              <div className="text-sm mt-2" style={{color:"var(--muted)"}}>{p.excerpt}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* QUIET DETAIL */}
      <section className="page-container">
        <div className="parchment-card p-8 md:p-12 grid md:grid-cols-[1fr_1.2fr] gap-8 items-center">
          <img src={ORCHID} alt="orchid" className="rounded-sm object-cover h-full w-full max-h-[420px]" />
          <div>
            <div className="page-eyebrow">a quiet word</div>
            <div className="tavern-heading text-3xl md:text-4xl gold-text mt-2">A sanctuary for slow nights</div>
            <p className="mt-4 leading-relaxed" style={{color:"var(--muted)"}}>
              Nocturne Orchid is not a brand or a business. It's the digital shape of a hobby, a lantern lit for
              the kinds of people who like to stay up late reading about old maps and older constellations.
            </p>
            <p className="mt-3 tavern-script text-2xl" style={{color:"var(--orchid-light)"}}>
              come in, take off your cloak — there's tea.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
